import type { CampaignManagementApi } from '~/services/campaign-management-api/campaign-management-api.types';

import type { IncentiveFormValues } from '../schemas/incentiveSchema';
import type { IncentiveFilters, IncentiveListItem, IncentiveNullableFieldState } from '../types';

type IncentiveListFilterParams = Pick<CampaignManagementApi.ListIncentivesParams, 'status'>;

export function createInitialFieldState(): IncentiveNullableFieldState {
  return {
    acceptance_criteria: 'unchanged',
    banner_file_external_id: 'unchanged',
    business_rules: 'unchanged',
    trigger_description: 'unchanged',
  };
}

export function mapCreateIncentivePayload(
  form: IncentiveFormValues,
): CampaignManagementApi.CreateIncentiveRequest {
  const payload: CampaignManagementApi.CreateIncentiveRequest = {
    description_ptbr: form.description_ptbr.trim(),
    name: form.name.trim(),
    title_ptbr: form.title_ptbr.trim(),
  };

  if (form.business_rules?.trim()) payload.business_rules = form.business_rules.trim();
  if (form.trigger_description?.trim()) payload.trigger_description = form.trigger_description.trim();
  if (form.acceptance_criteria?.trim()) payload.acceptance_criteria = form.acceptance_criteria.trim();
  if (form.banner_file_external_id) payload.banner_file_external_id = form.banner_file_external_id;

  return payload;
}

export function mapIncentiveDetailToFormData(
  detail: CampaignManagementApi.IncentiveDetail,
): IncentiveFormValues {
  return {
    acceptance_criteria: detail.acceptance_criteria,
    banner_file_external_id: detail.banner_file_external_id,
    business_rules: detail.business_rules,
    description_ptbr: detail.description_ptbr,
    name: detail.name,
    title_ptbr: detail.title_ptbr,
    trigger_description: detail.trigger_description,
  };
}

export function mapIncentiveFiltersToApiParams(
  filters: IncentiveFilters,
): IncentiveListFilterParams {
  return {
    ...(filters.status ? { status: filters.status } : {}),
  };
}

export function mapIncentiveListItem(
  item: CampaignManagementApi.IncentiveListItem,
): IncentiveListItem {
  return {
    acceptanceCriteria: item.acceptance_criteria,
    activedAt: item.actived_at,
    activedBy: item.actived_by,
    archivedAt: item.archived_at,
    bannerFileExternalId: item.banner_file_external_id,
    businessRules: item.business_rules,
    campaignsCount: item.campaigns_count,
    createdAt: item.created_at,
    createdBy: item.created_by,
    descriptionPtbr: item.description_ptbr,
    id: item.id,
    name: item.name,
    status: item.status,
    titlePtbr: item.title_ptbr,
    triggerDescription: item.trigger_description,
    updatedAt: item.updated_at,
  };
}

/**
 * The list endpoint returns the full CampaignIncentive field set per item (v2), so this
 * is an exact reshape — not a placeholder — letting the detail page render from the
 * cached list item alone, with no follow-up GET /incentives/{id} call.
 */
export function mapIncentiveListItemToDetail(
  item: IncentiveListItem,
): CampaignManagementApi.IncentiveDetail {
  return {
    acceptance_criteria: item.acceptanceCriteria,
    actived_at: item.activedAt,
    actived_by: item.activedBy,
    archived_at: item.archivedAt,
    banner_file_external_id: item.bannerFileExternalId,
    business_rules: item.businessRules,
    created_at: item.createdAt,
    created_by: item.createdBy,
    description_ptbr: item.descriptionPtbr,
    id: item.id,
    name: item.name,
    status: item.status,
    title_ptbr: item.titlePtbr,
    trigger_description: item.triggerDescription,
    updated_at: item.updatedAt,
  };
}

export function mapIncentiveListResponse(data: CampaignManagementApi.ListIncentivesData) {
  return {
    incentives: data.items.map(mapIncentiveListItem),
    page: data.pagination.page,
    pageSize: data.pagination.page_size,
    totalItems: data.pagination.total_items,
    totalPages: data.pagination.total_pages,
  };
}

/**
 * Builds the PATCH payload from an omit-vs-null diff.
 *
 * Plain string fields (name/title_ptbr/description_ptbr) are included only when they
 * differ from the original. Nullable fields are driven by an explicit `fieldState`
 * (set via a "Limpar" button distinct from an empty input) so that "never touched",
 * "cleared", and "typed a value" are all distinguishable — a plain diff against the
 * original cannot tell "cleared" apart from "unchanged and already empty".
 */
export function mapUpdateIncentivePayload(
  form: IncentiveFormValues,
  original: CampaignManagementApi.IncentiveDetail,
  fieldState: IncentiveNullableFieldState,
): CampaignManagementApi.UpdateIncentiveRequest {
  const payload: CampaignManagementApi.UpdateIncentiveRequest = {};

  if (form.name.trim() !== original.name) payload.name = form.name.trim();
  if (form.title_ptbr.trim() !== original.title_ptbr) payload.title_ptbr = form.title_ptbr.trim();
  if (form.description_ptbr.trim() !== original.description_ptbr) {
    payload.description_ptbr = form.description_ptbr.trim();
  }

  (['business_rules', 'trigger_description', 'acceptance_criteria'] as const)
    .forEach((field) => {
      const state = fieldState[field];
      if (state === 'cleared') payload[field] = null;
      else if (state === 'value') payload[field] = form[field]?.trim() ?? undefined;
      // 'unchanged' -> omit entirely
    });

  // banner_file_external_id is an opaque external id, not free text — no trimming.
  if (fieldState.banner_file_external_id === 'cleared') {
    payload.banner_file_external_id = null;
  } else if (fieldState.banner_file_external_id === 'value') {
    payload.banner_file_external_id = form.banner_file_external_id ?? undefined;
  }

  return payload;
}
